/** Entity list table for Entity Structure (QOE-RECON-1 — extracted from
 *  EntityStructurePage.tsx to clear the FILE_SIZE_POLICY.md 400-line
 *  threshold). Pure presentation: delete confirmation + the API call stay
 *  in the parent page; this only calls back via onDelete. */
import { Network, Trash2 } from 'lucide-react'
import type { DealEntity } from '@/api/client'
import { methodLabel } from './shared'

export default function EntityTable({
  entities,
  canManage,
  deleting,
  onDelete,
}: {
  entities: DealEntity[]
  canManage: boolean
  deleting: string | null
  onDelete: (entityId: string) => void
}) {
  if (entities.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-lg p-10 text-center text-gray-400">
        <Network className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No entities added yet.</p>
        <p className="text-xs mt-1">Add at least 2 entities to run consolidation.</p>
      </div>
    )
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Entity</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Type</th>
            <th className="text-right px-4 py-2 font-medium text-gray-600">Ownership %</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Method</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Session</th>
            {canManage && <th className="px-4 py-2" />}
          </tr>
        </thead>
        <tbody className="divide-y">
          {entities.map(e => (
            <tr key={e.entity_id} className="hover:bg-gray-50">
              <td className="px-4 py-2.5">
                <span className="font-medium text-gray-900">{e.name}</span>
                {e.is_primary && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                    Primary
                  </span>
                )}
                {e.carved_out && (
                  <span
                    className="ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded"
                    title="Outside the deal perimeter — intercompany transactions with this entity are not eliminated"
                  >
                    Carved out
                  </span>
                )}
                {/* CONSOL-FIX-1 C-A — either control fact means the entity
                    consolidates regardless of ownership %, so the ownership
                    column alone would misrepresent the row. */}
                {(e.guarantees_debt || e.exists_to_serve_group) && (
                  <span
                    className="ml-2 text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded"
                    title={
                      e.guarantees_debt && e.exists_to_serve_group
                        ? 'Group guarantees its debt and it exists mainly to serve the group'
                        : e.guarantees_debt
                        ? 'Group guarantees its debt'
                        : 'Exists mainly to serve the group'
                    }
                  >
                    Controlled by facts
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 text-gray-600">{e.entity_type}</td>
              <td className="px-4 py-2.5 text-right text-gray-700">
                {e.ownership_pct.toFixed(1)}%
              </td>
              <td className="px-4 py-2.5 text-gray-600 text-xs">
                {methodLabel(e.consolidation_method)}
              </td>
              <td className="px-4 py-2.5 text-xs">
                {e.normalization_session_id ? (
                  <span className="text-emerald-600">Linked</span>
                ) : (
                  <span className="text-amber-500">Not linked</span>
                )}
              </td>
              {canManage && (
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => onDelete(e.entity_id)}
                    disabled={deleting === e.entity_id}
                    className="text-red-400 hover:text-red-600 disabled:opacity-40"
                    title="Remove entity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
